import { type ReactNode, useMemo } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { theme } from '@styles/theme';

type Direction = 'up' | 'down' | 'left' | 'right' | 'none';

interface FadeInProps {
  children: ReactNode;
  /** Direção do deslocamento inicial. */
  direction?: Direction;
  /** Distância em pixels do deslocamento. */
  distance?: number;
  /** Atraso antes de iniciar a animação. */
  delay?: number;
  /** Duração da animação. */
  duration?: number;
  /** Classe CSS extra. */
  className?: string;
  /** Tag HTML de renderização. */
  as?: keyof HTMLElementTagNameMap;
}

function getInitial(direction: Direction, distance: number) {
  switch (direction) {
    case 'up':    return { opacity: 0, y: distance };
    case 'down':  return { opacity: 0, y: -distance };
    case 'left':  return { opacity: 0, x: distance };
    case 'right': return { opacity: 0, x: -distance };
    case 'none':  return { opacity: 0 };
  }
}

/**
 * Anima seus filhos com fade + deslocamento quando o componente é montado.
 * Ideal para entradas de página e seções hero.
 * Respeita prefers-reduced-motion automaticamente.
 *
 * @example
 * <FadeIn delay={0.3} direction="up">
 *   <h1>Título</h1>
 * </FadeIn>
 */
export function FadeIn({
  children,
  direction = 'up',
  distance = 24,
  delay = 0,
  duration = theme.durations.slower,
  className,
  as = 'div',
}: FadeInProps) {
  const shouldReduceMotion = useReducedMotion();

  // motion(tag) cria um motion component a partir de qualquer tag HTML.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const MotionTag = useMemo(() => motion(as), [as]);

  return (
    <MotionTag
      className={className}
      initial={shouldReduceMotion ? { opacity: 0 } : getInitial(direction, distance)}
      animate={{ opacity: 1, x: 0, y: 0 }}
      transition={{
        duration: shouldReduceMotion ? theme.durations.fast : duration,
        delay,
        ease: theme.easings.smoothOut,
      }}
    >
      {children}
    </MotionTag>
  );
}
